import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Buildings, CaretDown, Storefront, Plus, CircleNotch } from "@phosphor-icons/react";
import serviceMeta from "../../data/serviceMeta.json";

// Map engagement.service_type to a catalog slug. service_type in Mongo can be
// either a bare form code ("1040") or a full label ("1040 Individual",
// "1120-S S-Corp"), so we normalise by taking the first token.
const FORM_TO_SLUG = {
  "1040": "tax-strategy",
  "1065": "tax-strategy",
  "1120": "tax-strategy",
  "1120-S": "tax-strategy",
  "1120S": "tax-strategy",
  "990": "tax-strategy",
  "1041": "estate-trust-planning",
};

function serviceTypeToSlug(serviceType) {
  const code = String(serviceType || "").trim().split(/\s+/)[0];
  return FORM_TO_SLUG[code] || FORM_TO_SLUG[code.replace(/-/g, "")] || null;
}

const CAT_ACCENT = {
  tax:           "bg-emerald-50 text-emerald-800 border-emerald-200",
  advisory:      "bg-indigo-50 text-indigo-800 border-indigo-200",
  accounting:    "bg-sky-50 text-sky-800 border-sky-200",
  compliance:    "bg-amber-50 text-amber-800 border-amber-200",
  international: "bg-amber-50 text-amber-800 border-amber-200",
  ai:            "bg-slate-900 text-white border-slate-900",
  custom:        "bg-zinc-100 text-zinc-700 border-zinc-200",
};

// Human-readable label for the pill (title-cased single word).
const CAT_LABEL = {
  tax: "Tax",
  advisory: "Advisory",
  accounting: "Accounting",
  compliance: "Compliance",
  international: "Int'l",
  ai: "AI",
  custom: "Custom",
};

// Derive the category for an engagement. Uses:
//   1. engagement.service_category if the engagement came from SERVICE_CATALOG
//   2. serviceMeta[slug].category for legacy tax service_types mapped via FORM_TO_SLUG
//   3. regex on service_type as a last-ditch inference ("1040 Individual" → tax)
function categoryFor(eng, meta) {
  if (eng.service_category) return eng.service_category;
  if (meta?.category) return meta.category;
  const st = String(eng.service_type || "").toLowerCase();
  if (/^(1040|1041|1065|1120|990|1040-nr)/.test(st) || /\btax\b/.test(st) || /schedule/.test(st)) return "tax";
  return "custom";
}

// Stage → live status badge. Pulsing dot on live/active states adds sophistication
// without being noisy.
const STAGE_STATUS = {
  lead:          { label: "Kick-off",         dot: "bg-zinc-400",    pulse: false },
  engagement:    { label: "Engaged",          dot: "bg-indigo-500",  pulse: false },
  organizer:     { label: "Organizer sent",   dot: "bg-indigo-500",  pulse: false },
  waiting_docs:  { label: "Awaiting docs",    dot: "bg-amber-500",   pulse: true  },
  preparation:   { label: "Return pending",   dot: "bg-emerald-500", pulse: true  },
  cpa_review:    { label: "CPA review",       dot: "bg-emerald-500", pulse: true  },
  client_review: { label: "Client review",    dot: "bg-amber-500",   pulse: true  },
  e8879:         { label: "Signing",          dot: "bg-amber-500",   pulse: true  },
  ready_efile:   { label: "Ready to e-file",  dot: "bg-emerald-500", pulse: true  },
  accepted:      { label: "Filed · Accepted", dot: "bg-emerald-600", pulse: false },
  archived:      { label: "Archived",         dot: "bg-zinc-400",    pulse: false },
};

export function PortalSidebar({ entities, entityId, onSelectEntity, engagements, onOpenService, onOpenCatalog }) {
  const activeServices = useMemo(() => {
    const catalog = serviceMeta.services || {};
    const seen = new Set();
    const out = [];
    for (const e of engagements || []) {
      const slug = serviceTypeToSlug(e.service_type);
      const key = slug || `eng-${e.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const meta = slug ? catalog[slug] : null;
      out.push({
        engagement_id: e.id,
        slug,
        stage: e.stage,
        service_type: e.service_type,
        tax_year: e.tax_year,
        title: meta?.title || e.service_type || "Service",
        category: categoryFor(e, meta),
      });
    }
    return out;
  }, [engagements]);

  const catalogCount = Object.keys(serviceMeta.services || {}).length;
  const showPortfolioOpt = (entities || []).length > 1;

  const currentEntity = (entities || []).find((e) => e.id === entityId);

  return (
    <aside className="hidden md:flex w-60 shrink-0 border-r border-zinc-200 bg-white min-h-[calc(100vh-65px)] sticky top-[65px] self-start flex-col" data-testid="portal-sidebar">
      {/* Entity Switcher */}
      <div className="p-3 border-b border-zinc-100">
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 px-1 mb-1.5">Entity</p>
        <div className="relative group">
          <select
            data-testid="entity-switcher"
            value={entityId || ""}
            onChange={(e) => onSelectEntity(e.target.value || null)}
            className="w-full appearance-none bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2 pr-7 text-xs font-medium text-zinc-800 hover:bg-white focus:outline-none focus:ring-1 focus:ring-slate-900 transition-colors duration-150 cursor-pointer">
            {showPortfolioOpt && <option value="">← All entities (Portfolio)</option>}
            {(entities || []).map((en) => (
              <option key={en.id} value={en.id}>{`${en.name} · ${en.entity_type}`}</option>
            ))}
          </select>
          <CaretDown size={12} weight="bold" className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
        </div>
        {currentEntity && (
          <p className="mt-1.5 flex items-center gap-1 text-[10px] text-zinc-400 px-1">
            <Buildings size={10} weight="fill" /> {currentEntity.entity_type}
          </p>
        )}
      </div>

      {/* Active Engagement Services */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 px-1 mb-2">Active engagement services</p>
        {activeServices.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-200 p-3 text-[11px] text-zinc-500 leading-relaxed" data-testid="sidebar-no-services">
            <CircleNotch size={14} className="text-zinc-400 mb-1" />
            No services engaged yet.
            <p className="text-zinc-400 mt-0.5">Your CPA will assign you a service package.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {activeServices.map((s) => {
              const status = STAGE_STATUS[s.stage] || STAGE_STATUS.engagement;
              return (
                <button
                  key={s.engagement_id}
                  data-testid={`sidebar-service-${s.slug || s.engagement_id}`}
                  onClick={() => onOpenService(s)}
                  className="w-full text-left rounded-md border border-zinc-200 hover:border-slate-900 bg-white hover:bg-zinc-50 hover:shadow-sm px-2.5 py-2 transition-all duration-200 group">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-medium text-slate-900 truncate flex-1">{s.title}</span>
                    <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1 py-0.5 ${CAT_ACCENT[s.category] || CAT_ACCENT.custom}`} data-testid={`sidebar-service-pill-${s.category}`}>{CAT_LABEL[s.category] || s.category}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1" data-testid={`sidebar-service-status-${s.engagement_id}`}>
                    <span className="relative flex h-1.5 w-1.5">
                      {status.pulse && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${status.dot} opacity-60`} />}
                      <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${status.dot}`} />
                    </span>
                    <span className="text-[9px] text-zinc-500 font-medium">{status.label}</span>
                    <span className="text-[9px] text-zinc-300 ml-auto">TY {s.tax_year}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Explore Catalog */}
      <div className="border-t border-zinc-100 p-3 space-y-2">
        <button
          onClick={() => onOpenCatalog?.()}
          data-testid="sidebar-explore-catalog-btn"
          className="flex items-center justify-between gap-1.5 w-full rounded-md bg-slate-900 text-white text-[11px] font-medium px-2.5 py-2 hover:bg-slate-800 hover:shadow-md transition-all duration-200">
          <span className="flex items-center gap-1.5"><Storefront size={13} weight="fill" /> Explore service catalog</span>
          <span className="text-[10px] text-zinc-300">→</span>
        </button>
        <button
          onClick={() => onOpenCatalog?.()}
          data-testid="sidebar-request-service-btn"
          className="flex items-center gap-1.5 w-full rounded-md border border-zinc-200 text-[11px] text-zinc-700 px-2.5 py-2 hover:bg-zinc-50 hover:border-slate-900 transition-colors duration-150">
          <Plus size={12} weight="bold" /> Request a new service
        </button>
      </div>
    </aside>
  );
}
